import * as React from 'react'
import type {
  CoverBlock,
  IrrigationFindingsBlock,
  IrrigationScheduleBlock,
  ProblemsBlock,
  ProposalDocumentDto,
  ProseBlock,
  SinceLastVisitBlock,
  FutureWorkBlock,
  SiteMapBlock,
  SummaryBlock,
  TreeScheduleBlock,
} from '@lib/dto/proposal-document.dto'
import { PROBLEM_SEVERITY_LABELS } from '@lib/enums/problem-severity.enum'
import { planLabel } from '@lib/enums/proposal-plan.enum'
import { SEASON_LABELS } from '@lib/enums/season.enum'
import { parseMoney } from '@lib/helpers/money'
import {
  TREE_PRIORITIES,
  planFigures,
  scheduleRowPriority,
  treePriority,
  type PlanFigures,
} from '@lib/helpers/proposal-plans'
import { cn } from '@/lib/utils'
import { PhotoSourceProvider, ProposalPhoto, type PhotoSource } from '@/proposal-document/Photo'
import { SitePlanSvg } from '@/proposal-document/SitePlanSvg'
import { Qr } from '@/proposal-document/Qr'
import { repairPriority } from '@/proposal-document/repairs'
import {
  PRIORITY_LEGEND,
  priorityClass,
  severityClass,
} from '@/proposal-document/canopy'
import {
  andList,
  hasOptionalWork,
  monthOf,
  recommendedOf,
  severityWords,
} from '@/proposal-document/chapters'
import { planKicker, wholeDollars, workCount } from '@/proposal-document/PlanChapter'
import { splitQuestion, splitReview } from '@/proposal-document/text'

/**
 * The proposal as slides, for the board meeting.
 *
 * A community manager presenting a bid on the clubhouse TV is the moment it
 * gets decided, and a scrolling document is the wrong shape for a room. The
 * same document becomes a handful of slides: the ask, the short version, the
 * property, the worst findings with their photos, the plans, why us, and next
 * steps with a code to open it on a phone. Nothing here is new content; it is
 * the document, paced for a room.
 */

interface Slide {
  key: string
  render: () => React.ReactNode
}

interface Parts {
  cover: CoverBlock | null
  summary: SummaryBlock | null
  maps: SiteMapBlock[]
  schedules: TreeScheduleBlock[]
  irrigationSchedules: IrrigationScheduleBlock[]
  problems: ProblemsBlock | null
  irrigationFindings: IrrigationFindingsBlock | null
  sinceLastVisit: SinceLastVisitBlock[]
  futureWork: FutureWorkBlock | null
  narrative: ProseBlock | null
  reviews: ProseBlock | null
  comparison: ProseBlock | null
  nextSteps: ProseBlock | null
}

function partsOf(document: ProposalDocumentDto): Parts {
  const parts: Parts = {
    cover: null,
    summary: null,
    maps: [],
    schedules: [],
    irrigationSchedules: [],
    problems: null,
    irrigationFindings: null,
    sinceLastVisit: [],
    futureWork: null,
    narrative: null,
    reviews: null,
    comparison: null,
    nextSteps: null,
  }
  for (const block of document.blocks) {
    if (block.type === 'cover') parts.cover = block
    else if (block.type === 'summary') parts.summary = block
    else if (block.type === 'siteMap') parts.maps.push(block)
    else if (block.type === 'treeSchedule') parts.schedules.push(block)
    else if (block.type === 'irrigationSchedule') parts.irrigationSchedules.push(block)
    else if (block.type === 'problems') parts.problems = block
    else if (block.type === 'irrigationFindings') parts.irrigationFindings = block
    else if (block.type === 'sinceLastVisit') parts.sinceLastVisit.push(block)
    else if (block.type === 'futureWork') parts.futureWork = block
    else if (block.type === 'prose') {
      if (block.role === 'reviews') parts.reviews = block
      else if (block.role === 'comparison') parts.comparison = block
      else if (block.role === 'nextSteps') parts.nextSteps = block
      else if (block.role === 'narrative' || (block.role === null && parts.narrative === null)) {
        parts.narrative = block
      }
    }
  }
  return parts
}

/** The evidence for one finding: the first of its trees' own photos. */
function findingPhoto(maps: SiteMapBlock[], name: string): string | null {
  for (const map of maps) {
    for (const tree of map.trees) {
      for (const problem of tree.problems) {
        if (problem.name === name && problem.photoDocumentIds[0] !== undefined) {
          return problem.photoDocumentIds[0]
        }
      }
    }
  }
  return null
}

/** The cover photo, or their site drawn when there is none. */
function titleMedia(cover: CoverBlock, parts: Parts): React.ReactNode {
  if (cover.coverPhotoDocumentId !== null) {
    return <ProposalPhoto documentId={cover.coverPhotoDocumentId} alt="" eager />
  }
  const map = parts.maps[0]
  if (map === undefined) return null
  return (
    <SitePlanSvg
      block={map}
      rows={parts.schedules[0]?.rows ?? []}
      irrigation={irrigationOf(parts, map.siteName)}
      aspectMax={0.8}
    />
  )
}

function irrigationOf(parts: Parts, site: string): IrrigationScheduleBlock | undefined {
  return parts.irrigationSchedules.find((schedule) => schedule.siteName === site)
}

function buildSlides(
  document: ProposalDocumentDto,
  figures: PlanFigures[],
  shareUrl: string | null,
): Slide[] {
  const parts = partsOf(document)
  const cover = parts.cover
  const hasOptional = hasOptionalWork(figures)
  const hasTrees = document.includesTrees
  const recommended = figures.find((figure) => figure.plan === recommendedOf(figures))
  // Whole dollars throughout: a room compares figures, it does not invoice.
  const due = (amount: string | null): string => wholeDollars(parseMoney(amount) ?? 0)
  const slides: Slide[] = []

  if (cover !== null) {
    slides.push({
      key: 'title',
      render: () => (
        <div className="deck-title">
          <div className="deck-title-media">{titleMedia(cover, parts)}</div>
          <div className="deck-title-body">
            <p className="deck-kicker deck-kicker--gold">Proposal for {cover.clientName}</p>
            <h1 className="deck-h1">{cover.title}</h1>
            <p className="deck-lede">
              {cover.preparedByName ?? cover.preparedByOrganization}
              {cover.preparedByCredential === null ? '' : `, ${cover.preparedByCredential}`}
            </p>
          </div>
        </div>
      ),
    })
  }

  const summary = parts.summary
  if (summary !== null) {
    const paragraph = parts.narrative?.paragraphs[0] ?? null
    slides.push({
      key: 'short',
      render: () => (
        <div className="deck-page">
          <p className="deck-kicker">The short version</p>
          {paragraph === null ? null : <p className="deck-text">{paragraph}</p>}
          <dl className="deck-figures">
            {!hasTrees ? null : (
              <div>
                <dt>Trees surveyed</dt>
                <dd>{summary.treeCount}</dd>
              </div>
            )}
            {(summary.irrigationRepairCount ?? 0) === 0 ? null : (
              <div>
                <dt>Irrigation repairs</dt>
                <dd>{summary.irrigationRepairCount}</dd>
              </div>
            )}
            {!hasTrees || summary.hazardTreeCount === null ? null : (
              <div>
                <dt>Hazards</dt>
                <dd>{summary.hazardTreeCount}</dd>
              </div>
            )}
            {recommended === undefined ? null : (
              <div>
                <dt>Recommended</dt>
                <dd>{due(recommended.amountDue)}</dd>
              </div>
            )}
          </dl>
        </div>
      ),
    })
  }

  // A plan with trees on it, or repairs pinned; irrigation sent before the plan drew it has neither.
  const shownMaps = parts.maps.filter(
    (map) => hasTrees || (map.irrigation?.repairs.length ?? 0) > 0,
  )
  for (const map of shownMaps) {
    const rows = parts.schedules.find((schedule) => schedule.siteName === map.siteName)?.rows ?? []
    const irrigation = irrigationOf(parts, map.siteName)
    const repairs = irrigation?.repairs ?? []
    const counts = TREE_PRIORITIES.map((priority) => ({
      priority,
      count:
        rows.filter((row) => scheduleRowPriority(row) === priority).length +
        repairs.filter((repair) => repairPriority(repair) === priority).length,
    }))
    slides.push({
      key: `plan-${map.siteName}`,
      render: () => (
        <div className="deck-split">
          <div className="deck-split-media deck-split-media--plan">
            <SitePlanSvg block={map} rows={rows} irrigation={irrigation} aspectMax={0.85} />
          </div>
          <div className="deck-split-body">
            <p className="deck-kicker">Your property</p>
            <h2 className="deck-h2">{map.siteName}</h2>
            <ul className="deck-counts">
              {counts.map(({ priority, count }) => (
                <li key={priority}>
                  <span className={cn('deck-swatch', priorityClass(priority))} aria-hidden />
                  <span className="deck-count">{count}</span>
                  <span>{PRIORITY_LEGEND[priority]}</span>
                </li>
              ))}
            </ul>
            <p className="deck-small">{planCaption(rows.length > 0, repairs.length > 0)}</p>
          </div>
        </div>
      ),
    })
  }

  const entries = parts.problems?.entries ?? []
  const lead = entries
    .filter((entry) => treePriority(entry.worstSeverity) === 'hazard')
    .slice(0, 3)
  for (const entry of lead) {
    const photo = findingPhoto(parts.maps, entry.name)
    slides.push({
      key: `finding-${entry.name}`,
      render: () => (
        <div className="deck-split">
          <div className="deck-split-media">
            {photo === null ? null : <ProposalPhoto documentId={photo} alt="" />}
          </div>
          <div className="deck-split-body">
            <p className="deck-kicker">What we found</p>
            {entry.worstSeverity === null ? null : (
              <p className={cn('proposal-severity', severityClass(entry.worstSeverity))}>
                {PROBLEM_SEVERITY_LABELS[entry.worstSeverity]}
              </p>
            )}
            <h2 className="deck-h2">{entry.name}</h2>
            {entry.description === null ? null : <p className="deck-text">{entry.description}</p>}
            {entry.treatments[0] === undefined ? null : (
              <p className="deck-text">
                <span className="deck-strong">What we would do:</span> {entry.treatments[0].label}
              </p>
            )}
            <p className="deck-small">
              {entry.treeLabels.length === 1 ? 'Tree' : 'Trees'} {entry.treeLabels.join(', ')}
            </p>
          </div>
        </div>
      ),
    })
  }
  const rest = entries.filter((entry) => !lead.includes(entry))
  if (rest.length > 0) {
    slides.push({
      key: 'also-found',
      render: () => (
        <div className="deck-page">
          <p className="deck-kicker">Also found</p>
          <ul className="deck-list">
            {rest.slice(0, 7).map((entry) => (
              <li key={entry.name}>
                <span className="deck-strong">{entry.name}</span>
                <span>
                  {entry.treeLabels.length} {entry.treeLabels.length === 1 ? 'tree' : 'trees'}
                  {severityWords(parts.maps, entry) === null ? '' : ` · ${String(severityWords(parts.maps, entry))}`}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ),
    })
  }

  /*
   * The irrigation, after the trees: its worst kinds of repair with their
   * photo, then the rest as a list, as the tree findings are paced.
   */
  const repairEntries = parts.irrigationFindings?.entries ?? []
  const repairLead = repairEntries
    .filter((entry) => treePriority(entry.worstSeverity) === 'hazard')
    .slice(0, 2)
  for (const entry of repairLead) {
    slides.push({
      key: `irrigation-${entry.name}`,
      render: () => (
        <div className="deck-split">
          <div className="deck-split-media">
            {entry.photoDocumentId === null ? null : (
              <ProposalPhoto documentId={entry.photoDocumentId} alt="" />
            )}
          </div>
          <div className="deck-split-body">
            <p className="deck-kicker">In the irrigation</p>
            {entry.worstSeverity === null ? null : (
              <p className={cn('proposal-severity', severityClass(entry.worstSeverity))}>
                {PROBLEM_SEVERITY_LABELS[entry.worstSeverity]}
              </p>
            )}
            <h2 className="deck-h2">{entry.name}</h2>
            {entry.description === null ? null : <p className="deck-text">{entry.description}</p>}
            <p className="deck-small">
              {entry.count} found{entry.zones.length === 0 ? '' : ` · ${entry.zones.join(', ')}`}
            </p>
          </div>
        </div>
      ),
    })
  }
  const repairRest = repairEntries.filter((entry) => !repairLead.includes(entry))
  if (repairRest.length > 0) {
    slides.push({
      key: 'irrigation-found',
      render: () => (
        <div className="deck-page">
          <p className="deck-kicker">{repairLead.length > 0 ? 'Also in the irrigation' : 'In the irrigation'}</p>
          <ul className="deck-list">
            {repairRest.slice(0, 7).map((entry) => (
              <li key={entry.name}>
                <span className="deck-strong">{entry.name}</span>
                <span>
                  {entry.count} found
                  {entry.worstSeverity === null ? '' : ` · ${PROBLEM_SEVERITY_LABELS[entry.worstSeverity]}`}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ),
    })
  }

  /*
   * Since our last visit, after what was found: a property visited before
   * hears what came back and what changed, one slide a site, as long as the
   * findings lists run.
   */
  for (const since of parts.sinceLastVisit) {
    const lines = [
      ...since.cameBack.map((entry) => ({
        key: `again-${entry.name}`,
        strong: entry.name,
        detail: `${andList(entry.where)} · also found in ${monthOf(entry.foundOn)}`,
      })),
      ...since.changes.map((line) => ({ key: `changed-${line}`, strong: null, detail: line })),
    ]
    slides.push({
      key: `since-${since.siteName}`,
      render: () => (
        <div className="deck-page">
          <p className="deck-kicker">
            {since.title ?? 'Since our last visit'} · {monthOf(since.since)}
            {parts.sinceLastVisit.length > 1 ? ` · ${since.siteName}` : ''}
          </p>
          <ul className="deck-list">
            {lines.slice(0, 7).map((line) => (
              <li key={line.key}>
                {line.strong === null ? null : <span className="deck-strong">{line.strong}</span>}
                <span>{line.detail}</span>
              </li>
            ))}
          </ul>
        </div>
      ),
    })
  }

  const offered = figures.filter((figure) => figure.isAvailable)
  if (offered.length > 0) {
    const homes = summary?.homeCount ?? null
    slides.push({
      key: 'plans',
      render: () => (
        <div className="deck-page">
          <p className="deck-kicker">{offered.length > 1 ? 'Choose a plan' : 'The price'}</p>
          <div className="deck-plans">
            {offered.map((figure) => {
              const now = parseMoney(figure.amountDue) ?? 0
              return (
                <div
                  key={figure.plan}
                  className={cn('deck-plan', figure.plan === recommendedOf(figures) && 'is-recommended')}
                >
                  <p className="deck-kicker">{planKicker(figure.plan, hasOptional)}</p>
                  <p className="deck-plan-name">{planLabel(figure.plan, hasOptional)}</p>
                  <p className="deck-plan-price">{wholeDollars(now)}</p>
                  <p className="deck-small">
                    {workCount(figure.treeCount, figure.repairCount)}
                    {homes === null ? '' : ` · ${wholeDollars(now / homes)} per home`}
                  </p>
                </div>
              )
            })}
          </div>
        </div>
      ),
    })
  }

  /*
   * Later seasons, after the price: what is coming and roughly what it
   * costs, said once so a board does not mistake it for part of the ask.
   */
  const futureWork = parts.futureWork
  if (futureWork !== null) {
    slides.push({
      key: 'later',
      render: () => (
        <div className="deck-page">
          <p className="deck-kicker">{futureWork.title ?? 'Future work'}</p>
          <ul className="deck-list">
            {futureWork.seasons.map((season) => {
              const trees = season.sites.reduce(
                (count, site) => count + site.trees.length,
                0,
              )
              return (
                <li key={season.season}>
                  <span className="deck-strong">{SEASON_LABELS[season.season]}</span>
                  <span>
                    {trees} {trees === 1 ? 'tree' : 'trees'}
                    {season.amount === null
                      ? ''
                      : ` · about ${wholeDollars(parseMoney(season.amount) ?? 0)}`}
                  </span>
                </li>
              )
            })}
          </ul>
          <p className="deck-small">
            Estimates, rebid when each season comes. Not part of what you approve
            today.
          </p>
        </div>
      ),
    })
  }

  if (cover !== null) {
    const review = parts.reviews?.bullets[0]
    const questions = (parts.comparison?.bullets ?? []).slice(0, 4)
    slides.push({
      key: 'why',
      render: () => (
        <div className="deck-page deck-page--two">
          <div>
            <p className="deck-kicker">Why us</p>
            <h2 className="deck-h2">{cover.preparedByName ?? cover.preparedByOrganization}</h2>
            {cover.preparedByCredential === null ? null : (
              <p className="deck-small">{cover.preparedByCredential}</p>
            )}
            {review === undefined ? null : (
              <blockquote className="deck-quote">
                {splitReview(review).quote}
                <cite>{splitReview(review).attribution}</cite>
              </blockquote>
            )}
          </div>
          <ul className="deck-list">
            {questions.map((bullet) => {
              const { question, answer } = splitQuestion(bullet)
              return (
                <li key={bullet}>
                  <span className="deck-strong">{question}</span>
                  {answer === null ? null : <span>{answer}</span>}
                </li>
              )
            })}
          </ul>
        </div>
      ),
    })
  }

  slides.push({
    key: 'next',
    render: () => (
      <div className="deck-page deck-page--two">
        <div>
          <p className="deck-kicker">Next steps</p>
          <ol className="deck-steps">
            {(parts.nextSteps?.bullets ?? []).map((bullet, index) => (
              <li key={bullet}>
                <span className="deck-step-num">{index + 1}</span>
                {bullet}
              </li>
            ))}
          </ol>
          {cover?.phone == null ? null : <p className="deck-phone">Call {cover.phone}</p>}
        </div>
        {shareUrl === null ? null : (
          <div className="deck-qr">
            <Qr value={shareUrl} className="deck-qr-code" />
            <p className="deck-small">Scan to open the proposal, choose a plan and accept.</p>
          </div>
        )}
      </div>
    ),
  })

  return slides
}

/**
 * Full screen for the slides, when the browser allows it; a window is fine
 * when it does not. Call it from the click that opens them: browsers refuse
 * full screen that is not part of a click, and by the time the slides render
 * it no longer is. The whole page goes full screen and the slides cover it.
 */
export function enterFullScreen(): void {
  window.document.documentElement.requestFullscreen?.().catch(() => undefined)
}

export function Presentation({
  document,
  photoSource,
  shareUrl,
  onClose,
}: {
  document: ProposalDocumentDto
  photoSource: PhotoSource
  shareUrl: string | null
  onClose: () => void
}) {
  const slides = React.useMemo(
    () => buildSlides(document, planFigures(document), shareUrl),
    [document, shareUrl],
  )
  const [index, setIndex] = React.useState(0)
  const rootRef = React.useRef<HTMLDivElement | null>(null)

  const go = React.useCallback(
    (next: number) => {
      setIndex(Math.max(0, Math.min(slides.length - 1, next)))
    },
    [slides.length],
  )

  React.useEffect(() => {
    rootRef.current?.focus()
    return () => {
      // Loose on purpose: iPhone Safari has no element full screen, and there
      // this is undefined rather than null, with no exitFullscreen to call.
      if (window.document.fullscreenElement != null) {
        window.document.exitFullscreen().catch(() => undefined)
      }
    }
  }, [])

  function onKeyDown(event: React.KeyboardEvent): void {
    if (event.key === 'Escape') onClose()
    else if (['ArrowRight', 'PageDown', ' ', 'Enter'].includes(event.key)) go(index + 1)
    else if (['ArrowLeft', 'PageUp', 'Backspace'].includes(event.key)) go(index - 1)
    else if (event.key === 'Home') go(0)
    else if (event.key === 'End') go(slides.length - 1)
    else return
    event.preventDefault()
  }

  const slide = slides[index]

  return (
    <PhotoSourceProvider source={photoSource}>
      <div
        ref={rootRef}
        className="deck print:hidden"
        role="dialog"
        aria-modal="true"
        aria-label="Proposal presentation"
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <div className="deck-stage" aria-live="polite">
          {slide === undefined ? null : <div className="deck-slide">{slide.render()}</div>}
        </div>
        <div className="deck-bar">
          <button type="button" onClick={onClose} className="deck-button">
            Close
          </button>
          <span className="deck-count-label">
            {index + 1} / {slides.length}
          </span>
          <span className="deck-nav">
            <button type="button" className="deck-button" disabled={index === 0} onClick={() => { go(index - 1) }}>
              ← Back
            </button>
            <button
              type="button"
              className="deck-button deck-button--gold"
              disabled={index === slides.length - 1}
              onClick={() => {
                go(index + 1)
              }}
            >
              Next →
            </button>
          </span>
        </div>
      </div>
    </PhotoSourceProvider>
  )
}

/** What the plan slide's counts are of. */
function planCaption(hasTrees: boolean, hasRepairs: boolean): string {
  if (hasTrees && hasRepairs) {
    return 'Every tree and irrigation repair recorded on site, numbered on the plan.'
  }
  return hasRepairs
    ? 'Every irrigation repair recorded on site, numbered R1, R2 and on.'
    : 'Every tree recorded on site and numbered on the plan.'
}
